
export type TokenizedResourceString = Array<string | number>;

export class MessagesTokenizer {
    private i = 0;
    private j = 0;
    private readonly arr: TokenizedResourceString = [];
    private index = 0;

    private handleQuotes(v: string): string {
        let value = v;

        // last quote in the string
        if (this.i === value.length - 1) {
            return value.substring(0, this.i); // Silently drop the trailing quote
        }

        // Handle two quotes in a row: "John''s"
        if (value.charAt(this.i + 1) === '\'') {
            return value.substring(0, this.i) + value.substring(++this.i); // Escaped quote
        }

        // Quoted string
        this.j = this.i + 2;
        while ((this.j = value.indexOf('\'', this.j)) !== -1) {
            if (this.j === value.length - 1 || value.charAt(this.j + 1) !== '\'') {
                // Found start and end quotes. Remove them
                value = value.substring(0, this.i) + value.substring(this.i + 1, this.j) + value.substring(this.j + 1);
                this.i = this.j - 1;
                break;
            } else {
                // Found a double quote, reduce to a single quote.
                value = value.substring(0, this.j) + value.substring(++this.j);
            }
        }

        if (this.j === -1) {
            // There is no end quote. Drop the start quote
            value = value.substring(0, this.i) + value.substring(this.i + 1);
        }

        return value;
    }

    private handlePlaceholder(v: string): string {
        let value = v;

        this.j = value.indexOf('}', this.i + 1);
        if (this.j === -1) {
            this.i++; // No end. Process the rest of the line. Java would throw an exception
            return value;
        }

        // Add 1 to the index so that it aligns with the function arguments.
        this.index = parseInt(value.substring(this.i + 1, this.j));
        if (!isNaN(this.index) && this.index >= 0) {
            // Put the line thus far (if it isn't empty) into the array
            const s = value.substring(0, this.i);
            if (s !== "") {
                this.arr.push(s);
            }
            // Put the parameter reference into the array
            this.arr.push(this.index);
            // Start the processing over again starting from the rest of the line.
            this.i = 0;
            value = value.substring(this.j + 1);
        } else {
            this.i = this.j + 1; // Invalid parameter. Leave as is.
        }

        return value;
    }

    private process(v: string): TokenizedResourceString {
        if (!v) {
            return [];
        }
        let value = v;

        // Lazily convert the string to a list of tokens.
        while (this.i < value.length) {
            if (value.charAt(this.i) === '\'') {
                // Handle quotes
                value = this.handleQuotes(value);
            } else if (value.charAt(this.i) === '{') {
                // Beginning of an unquoted placeholder.
                value = this.handlePlaceholder(value);
            } else {
                this.i++;
            }
        }

        // Put the remainder of the non-empty line into the array.
        if (value !== "") {
            this.arr.push(value);
        }

        return this.arr;
    }

    static process(value: string): TokenizedResourceString {
        return new MessagesTokenizer().process(value);
    }
}
